// This component is a rewrite of the DevExpress ToggleButton component to ensure a consistent styling of the underlying IconButton.
// The component is used as part of the ColumnChooser component in the grid. An example of the original component can be found here:
// https://github.com/DevExpress/devextreme-reactive/blob/master/packages/dx-react-grid-material-ui/src/templates/column-chooser/toggle-button.jsx

import { IconButton, IconSize, IIconButtonProps, makeStyles, theme, Tooltip } from '@archer-technologies/ui-components';
import { ColumnChooser } from '@devexpress/dx-react-grid-material-ui';
import { Popover } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import * as React from 'react';

const useStyles = makeStyles(() => {
  return {
    checkbox: {
      padding: 0
    },
    itemText: {
      paddingLeft: theme.spacing(1),
      '& span': {
        fontSize: '0.875rem'
      }
    },
    chooserButton: {
      marginLeft: '0.5rem'
    }
  };
});

const ToggleButtonComponent = ({ onToggle, getMessage, buttonRef, active, ...restProps }: ColumnChooser.ToggleButtonProps) => {
  const { chooserButton } = useStyles();
  const message = getMessage('showColumnChooser');
  const tooltipProps = {
    title: message,
    enterDelay: 300
  };
  const iconButtonProps: IIconButtonProps = {
    ...restProps,
    icon: 'Visibility',
    label: message,
    fontSize: 'large' as IconSize,
    onClick: onToggle
  };
  return (
    <Tooltip {...tooltipProps} placement="bottom">
      <IconButton {...iconButtonProps} ref={buttonRef} color="primary" className={active ? `focusActive ${chooserButton}` : `${chooserButton}`} />
    </Tooltip>
  );
};

export { ToggleButtonComponent };
export { ColumnChooserOverlay };
export { ColumnChooserItem };

const ColumnChooserOverlay = ({ visible, onHide, children, target, ...restProps }: ColumnChooser.OverlayProps) => {
  return (
    <Popover
      open={visible}
      anchorEl={target as Element}
      onClose={onHide}
      PaperProps={{ elevation: 0, style: { boxShadow: '0 2px 10px 0 rgba(33, 33, 33, .40)' } }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      {...restProps}
    >
      {children}
    </Popover>
  );
};

const ColumnChooserItem = ({ item: { column, hidden }, disabled, onToggle, ...restProps }: ColumnChooser.ItemProps) => {
  const { checkbox, itemText } = useStyles();
  if (column.name !== 'actions') {
    return (
      <ListItem key={column.name} button role="listitem" component="li" disabled={disabled} onClick={onToggle} {...restProps}>
        <Checkbox
          checked={!hidden}
          inputProps={{ 'aria-label': column.title || column.name }}
          tabIndex={-1}
          disableRipple
          disabled={disabled}
          className={checkbox}
        />
        <ListItemText className={itemText} primary={column.title || column.name} />
      </ListItem>
    );
  } else {
    return null;
  }
};
